import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Turnover Type Enum
// ============================================================================

/**
 * Valid turnover types from Enable historical data CSV.
 * - Non Rebateable Direct: Items that should NOT qualify for rebates (direct purchases)
 * - Non Rebateable Stock: Items that should NOT qualify for rebates (stock purchases)
 * - Rebateable Direct: Standard rebateable items
 * - Rebateable Stock: Stock/inventory rebateable items
 * - Turnover Adjustments: Rebateable items with adjustments (e.g., returns - can be negative values)
 */
export const TurnoverTypeEnum = z.enum([
  "Non Rebateable Direct",
  "Non Rebateable Stock",
  "Rebateable Direct",
  "Rebateable Stock",
  "Turnover Adjustments",
]);

export type TurnoverType = z.infer<typeof TurnoverTypeEnum>;

export const REBATEABLE_TURNOVER_TYPES: TurnoverType[] = [
  "Rebateable Direct",
  "Rebateable Stock",
  "Turnover Adjustments",
];

// Non-rebateable turnover types (both map to null product group)
export const NON_REBATEABLE_TURNOVER_TYPES: TurnoverType[] = [
  "Non Rebateable Direct",
  "Non Rebateable Stock",
];

// ============================================================================
// Step 1: Get Agreement Context
// ============================================================================

export const GetAgreementImportContextRequestSchema = z.object({
  agreementId: z.uuid(),
});

export const GetAgreementImportContextResponseSchema = z.object({
  agreementId: z.uuid(),
  agreementName: z.string(),
  supplierId: z.uuid(),
  supplierName: z.string(),
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  rexelCode: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
});

// ============================================================================
// Step 2: Parse CSV and Extract Product Codes
// ============================================================================

export const ParseHistoricalCsvRequestSchema = z.object({
  agreementId: z.uuid(),
  rexelCode: z.string(),
  year: z.int().min(2000).max(2100).prefault(2025),
});

export const CsvProductCodeSchema = z.object({
  product: z.string(),
  productReference: z.string(),
  transactionCount: z.number(),
  totalValue: z.number(),
  // Unique rebate rates from daily earnings CSV for this product
  historicalRates: z.array(z.number()),
  // Turnover type from the CSV - determines if product is rebateable
  turnoverType: TurnoverTypeEnum,
});

// Breakdown of rows/value by turnover type
export const TurnoverTypeBreakdownSchema = z.object({
  turnoverType: TurnoverTypeEnum,
  rowCount: z.number(),
  totalValue: z.number(),
  productCount: z.number(),
});

// Enable rule summary from Daily Earnings CSV
export const EnableRuleSummarySchema = z.object({
  // Program line ID from the CSV
  programLineId: z.string(),
  // Total actual earnings (sum of "Actual Earnings" column)
  totalActualEarnings: z.number(),
  // Total transactions volume (sum of "Total Transactions" column)
  totalTransactions: z.number(),
  // Unique rebate rates seen for this rule
  uniqueRates: z.array(z.number()),
  // Count of rows in daily earnings for this rule
  rowCount: z.number(),
});

export const ParseHistoricalCsvResponseSchema = z.object({
  totalRowsInFile: z.number(),
  filteredRows: z.number(),
  skippedRows: z.number(),
  productCodes: z.array(CsvProductCodeSchema),
  divisions: z.array(z.string()),
  dateRange: z.object({
    earliest: z.string(),
    latest: z.string(),
  }),
  earningsDateRange: z.object({
    earliest: z.string(),
    latest: z.string(),
  }),
  sampleRows: z.array(z.record(z.string(), z.string())).max(10),
  // Breakdown by turnover type
  turnoverTypeBreakdown: z.array(TurnoverTypeBreakdownSchema),
  // Enable rules summary from Daily Earnings CSV
  enableRules: z.array(EnableRuleSummarySchema),
});

// ============================================================================
// Step 3: Get Product Groups for Mapping
// ============================================================================

export const GetProductGroupsForMappingRequestSchema = z.object({
  supplierId: z.uuid(),
});

export const ProductGroupForMappingSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  isCatchAll: z.boolean(),
  existingMappedCodes: z.array(z.string()),
});

export const GetProductGroupsForMappingResponseSchema = z.object({
  productGroups: z.array(ProductGroupForMappingSchema),
});

// ============================================================================
// Step 4: Import Transactions
// ============================================================================

export const ProductCodeMappingSchema = z.object({
  productReference: z.string(),
  productGroupId: z.uuid().nullable(),
});

export const ImportHistoricalTransactionsRequestSchema = z.object({
  agreementId: z.uuid(),
  rexelCode: z.string(),
  year: z.int(),
  productCodeMappings: z.array(ProductCodeMappingSchema),
  saveMappingsToProductGroups: z.boolean().prefault(true),
});

// Per-rule breakdown from forecast calculation
export const RuleBreakdownSchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  totalVolume: z.number(),
  totalRebate: z.number(),
  discountPercentage: z.number(), // e.g. 2.5 for 2.5%
});

export const ImportHistoricalTransactionsResponseSchema = z.object({
  success: z.boolean(),
  invoicesCreated: z.number(),
  lineItemsCreated: z.number(),
  totalValue: z.number(),
  skippedDuplicates: z.number(),
  forecastReset: z.boolean(),
  // Comparison data: sum of actual earnings from daily earnings CSV
  actualEarningsSum: z.number(),
  // Forecast total after recalculation (null if not yet calculated)
  forecastTotal: z.number().nullable(),
  // Cleanup stats - data removed before import
  invoicesDeleted: z.number(),
  lineItemsDeleted: z.number(),
  associationsRemoved: z.number(),
  // Per-rule breakdown from forecast (null if forecast calculation failed)
  ruleBreakdown: z.array(RuleBreakdownSchema).nullable(),
});

// ============================================================================
// Step 5: AI-Powered Product Mapping Suggestions
// ============================================================================

export const SuggestProductMappingsRequestSchema = z.object({
  supplierId: z.uuid(),
  productCodes: z.array(
    z.object({
      productReference: z.string(),
      product: z.string(), // Product name for better AI matching
      turnoverType: TurnoverTypeEnum,
    }),
  ),
});

export const ProductMappingSuggestionSchema = z.object({
  productReference: z.string(),
  suggestedProductGroupId: z.uuid().nullable(),
  confidence: z.enum(["high", "medium", "low"]),
});

export const SuggestProductMappingsResponseSchema = z.object({
  suggestions: z.array(ProductMappingSuggestionSchema),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const historicalImportContracts = {
  getAgreementContext: oc
    .input(GetAgreementImportContextRequestSchema)
    .output(GetAgreementImportContextResponseSchema)
    .route({
      method: "GET",
      summary: "Get rebate agreement details for history import",
      description:
        "Returns the rebate agreement, supplier, company, supplier code, and date range used to set up a historical rebate transaction import.",
    }),

  parseCsv: oc
    .input(ParseHistoricalCsvRequestSchema)
    .output(ParseHistoricalCsvResponseSchema)
    .route({
      method: "POST",
      summary: "Preview historical rebate transactions for import",
      description:
        "Reads the stored historical transactions and daily earnings files for a supplier code and year, returning product codes, turnover-type breakdown, divisions, date ranges, and earnings by rule. Saves nothing.",
    }),

  getProductGroupsForMapping: oc
    .input(GetProductGroupsForMappingRequestSchema)
    .output(GetProductGroupsForMappingResponseSchema)
    .route({
      method: "GET",
      summary: "List supplier product groups for mapping",
      description:
        "Lists a supplier's rebate product groups with descriptions, catch-all flag, and product codes already mapped to each, for mapping imported product codes.",
    }),

  importTransactions: oc
    .input(ImportHistoricalTransactionsRequestSchema)
    .output(ImportHistoricalTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Import historical rebate transactions",
      description:
        "Replaces an agreement's imported transactions for a year with the historical file, using confirmed product-group mappings (optionally saved). Resets and regenerates the forecast; returns counts and per-rule totals.",
    }),

  suggestMappings: oc
    .input(SuggestProductMappingsRequestSchema)
    .output(SuggestProductMappingsResponseSchema)
    .route({
      method: "POST",
      summary: "Suggest product group mappings with AI",
      description:
        "Uses AI to suggest which supplier rebate product group each imported product code belongs to, with high, medium, or low confidence. Non-rebateable codes map to none. Saves nothing.",
    }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type GetAgreementImportContextRequest = z.infer<
  typeof GetAgreementImportContextRequestSchema
>;
export type GetAgreementImportContextResponse = z.infer<
  typeof GetAgreementImportContextResponseSchema
>;
export type ParseHistoricalCsvRequest = z.infer<
  typeof ParseHistoricalCsvRequestSchema
>;
export type ParseHistoricalCsvResponse = z.infer<
  typeof ParseHistoricalCsvResponseSchema
>;
export type CsvProductCode = z.infer<typeof CsvProductCodeSchema>;
export type GetProductGroupsForMappingRequest = z.infer<
  typeof GetProductGroupsForMappingRequestSchema
>;
export type GetProductGroupsForMappingResponse = z.infer<
  typeof GetProductGroupsForMappingResponseSchema
>;
export type ProductGroupForMapping = z.infer<
  typeof ProductGroupForMappingSchema
>;
export type ProductCodeMapping = z.infer<typeof ProductCodeMappingSchema>;
export type ImportHistoricalTransactionsRequest = z.infer<
  typeof ImportHistoricalTransactionsRequestSchema
>;
export type ImportHistoricalTransactionsResponse = z.infer<
  typeof ImportHistoricalTransactionsResponseSchema
>;
export type SuggestProductMappingsRequest = z.infer<
  typeof SuggestProductMappingsRequestSchema
>;
export type SuggestProductMappingsResponse = z.infer<
  typeof SuggestProductMappingsResponseSchema
>;
export type ProductMappingSuggestion = z.infer<
  typeof ProductMappingSuggestionSchema
>;
export type TurnoverTypeBreakdown = z.infer<typeof TurnoverTypeBreakdownSchema>;
export type EnableRuleSummary = z.infer<typeof EnableRuleSummarySchema>;
export type RuleBreakdown = z.infer<typeof RuleBreakdownSchema>;
